/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import PublicErrorPage from './components/public/PublicErrorPage';
import AppErrorBoundary from './components/public/AppErrorBoundary';
import React, { useEffect } from 'react';
// import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { useAppBranding } from './hooks/useAppBranding';
import { applyFavicon } from './utils/seoHelper';

import { ThemeProvider, useTheme } from './theme/ThemeContext';
import Home from './pages/Home';
// import Dashboard from './pages/Dashboard'; // old Dashboard retired — see the /dashboard route
import DashboardBody from './pages/DashboardBody';
import Settings from './pages/Settings';
import SignInPage from './pages/SignInPage';
import TableListPage from './pages/TableListPage';
import SignUpPage from './pages/SignUpPage';
import { AlertDialogProvider } from './components/common/AlertDialogContext';
import TableViewPage from './pages/TableViewPage';
import RecordDetailsPage from './pages/RecordDetailsPage';
import DynamicCreateEditForm from './pages/DynamicCreateEditForm'
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ExportsPage from './pages/ExportsPage';
import PrivateRoute from './components/PrivateRouter';
import UserProfilePage from './pages/UserProfilePage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import CheckoutReturnPage from './pages/CheckoutReturnPage';
import PublicTableViewPage from './pages/PublicTableViewPage';
import PublicRecordDetailRoute from './pages/PublicRecordDetailRoute';
import PublicPageView from './pages/PublicPageView';
import PublicLayout from './components/public/PublicLayout';
import { PublicAppProvider } from './context/PublicAppContext';
import NotificationToast from './components/NotificationToast';
import PreviewPage from './pages/PreviewPage';
import HeaderFooterPreviewPage from './pages/HeaderFooterPreviewPage';
import SsoCallbackPage from './pages/SsoCallbackPage';
import OrganizerRoute from './components/organizer/OrganizerRoute';
import OrganizerLayout from './pages/organizer/OrganizerLayout';
import OrganizerHome from './pages/organizer/OrganizerHome';
import OrganizerPlaceholder from './pages/organizer/OrganizerPlaceholder';
import OrganizerEventsPage from './pages/organizer/OrganizerEventsPage';
import OrganizerEventFormPage from './pages/organizer/OrganizerEventFormPage';
import OrganizerCheckInPage from './pages/organizer/OrganizerCheckInPage';
// Motion board: where an organizer marks a podium and publishes it.
import OrganizerResultsPage from './pages/organizer/OrganizerResultsPage';
import PublishResultsPage from './pages/organizer/PublishResultsPage';
import OrganizerRouteBuilderPage from './pages/organizer/OrganizerRouteBuilderPage';
import FeedLayout from './pages/feed/FeedLayout';
import FeedHome from './pages/feed/FeedHome';
// import { FeedProfilePage, FeedNotificationsPage, FeedAnalyticsPage } from './pages/feed/FeedSubPages';
import {
  FeedProfilePage, FeedAnalyticsPage, FeedEventsPage, FeedPassesPage,
} from './pages/feed/FeedSubPages';
import MyCertificatesPage, { CertificateLinkPage } from './pages/feed/certificates/MyCertificatesPage';
import MyPaymentsPage from './pages/feed/payments/MyPaymentsPage';
// Motion board, phase 2: connects a recorded run to a race the athlete entered.
// Hidden 2026-11-02, phase 7a — organizers attach in this phase, not athletes.
// import AttachRunPage from './pages/feed/results/AttachRunPage';
// Motion board: the public results board for one race. No login required.
import LeaderboardPage from './pages/feed/results/LeaderboardPage';
// Motion board: the list of every event's board. Public, for the same reason.
import ResultsIndexPage from './pages/feed/results/ResultsIndexPage';
// The run behind one result. A separate page, so a failure in it cannot reach
// the board that already works.
import RunDetailPage from './pages/feed/results/RunDetailPage';
import DashboardRedirect from './components/feed/DashboardRedirect';

// The organizer Home page is kept but hidden; see the /organizer index route.
const ORGANIZER_HOME_ENABLED = false;

function ThemeSwitcher() {
  const { theme, switchTheme } = useTheme();
  return (
    <button
      className="btn btn-outline-primary"
      style={{ margin: 12 }}
      onClick={switchTheme}
    >
      Switch Theme ({theme.name})
    </button>
  );
}

function App() {
  const { data: branding } = useAppBranding();

  useEffect(() => {
    applyFavicon(branding?.logo);
  }, [branding?.logo]);

  return (
    <AlertDialogProvider>
      {/* Optional: Add ThemeSwitcher globally */}
      {/* <ThemeSwitcher /> */}
      <NotificationToast />
      <AppErrorBoundary>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/auth/sso/callback/:providerKey" element={<SsoCallbackPage />} />

        {/* Page Builder preview routes — loaded in admin iframe, no layout wrapper */}
        <Route path="/p/preview" element={<PublicAppProvider><PreviewPage /></PublicAppProvider>} />
        <Route path="/p/hf-preview" element={<PublicAppProvider><HeaderFooterPreviewPage /></PublicAppProvider>} />

        {/* Public data table routes — standard navbar via PublicLayout */}
        <Route element={<PublicAppProvider><PublicLayout /></PublicAppProvider>}>
          {/* No index of every public table — a visitor reaches a table from a
              page that links to it, never by browsing the whole catalogue. */}
          <Route path="/p" element={<PublicErrorPage error={{ status: 404 }} />} />
          <Route path="/p/:tableSlug" element={<PublicTableViewPage />} />
          <Route path="/p/:tableSlug/:recordId" element={<PublicRecordDetailRoute />} />
          {/* Motion board. Public on purpose: a results list for a public event,
              readable with the app's own credentials and no personal login.
              /results is the way in — until it existed a board could only be
              reached by typing its id. */}
          <Route path="/results" element={<ResultsIndexPage />} />
          <Route path="/results/:eventActivityId" element={<LeaderboardPage />} />
          {/* The run behind one result — route, splits and climb. Public too:
              it is the same race record the board above already shows, read
              through two saved queries rather than a table, because the GPS
              track lives on the private `activity`. See runDetailApi. */}
          <Route path="/results/:eventActivityId/:eventResultId" element={<RunDetailPage />} />
        </Route>

        {/*
          Where a payment gateway drops the runner after checkout. Inside
          PublicAppProvider because it is a public landing page, and placed
          BEFORE the catch-all below, which would otherwise answer it with 404.
        */}
        <Route
          element={<PublicAppProvider><PublicLayout /></PublicAppProvider>}>
          <Route path="/checkout/return" element={<CheckoutReturnPage />} />
        </Route>

        {/* Page Builder custom pages — own DynamicHeader/Footer, no PublicLayout navbar. */}
        <Route element={<PublicAppProvider><Outlet /></PublicAppProvider>}>
          <Route path="/app/*" element={<PublicPageView />} />
        </Route>
        <Route path="/app" element={<PublicErrorPage error={{ status: 404 }} />} />
        <Route path="*" element={<PublicErrorPage error={{ status: 404 }} />} />

        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              {/* <Dashboard /> — the old Dashboard is retired: athletes use /feed and
                  organizers /organizer. DashboardRedirect sends every /dashboard/*
                  URL there, so the pages below never render and old links still work. */}
              <DashboardRedirect />
            </PrivateRoute>
          }>
          <Route path="" element={<DashboardBody />} />
          <Route path="tables" element={<TableListPage />} />
          <Route path="tables/:tableName" element={<TableViewPage />} />
          <Route path="tables/:tableName/:recordId" element={<RecordDetailsPage />} />
          <Route path="settings" element={<Settings />} />
          <Route path="tables/:tableName/create" element={<DynamicCreateEditForm />} />
          <Route path="tables/:tableName/:recordId/update" element={<DynamicCreateEditForm />} />
          <Route path="exports" element={<ExportsPage />} />
          <Route path="notifications" element={<NotificationPreferencesPage />} />
          <Route path="profile" element={<UserProfilePage />} />
        </Route>

        {/* Athlete surface — the home of every signed-in user without an
            organizer role (and of organizers who choose "athlete" at sign-in). */}
        <Route
          path="/feed"
          element={(
            <PrivateRoute>
              <FeedLayout />
            </PrivateRoute>
          )}
        >
          <Route path="" element={<FeedHome />} />
          <Route path="activity/:activityId" element={<FeedHome />} />
          <Route path="profile" element={<FeedProfilePage />} />
          {/* <Route path="notifications" element={<FeedNotificationsPage />} /> */}
          {/* Hidden from athletes for now: a typed or bookmarked URL — and the
              old /dashboard/notifications, which forwards here — lands on the feed. */}
          <Route path="notifications" element={<Navigate to="/feed" replace />} />
          <Route path="analytics" element={<FeedAnalyticsPage />} />
          {/* Phone/tablet only — the portal's Events and Registrations pages
              rendered inside the feed shell. On a desktop width these forward
              to /p/event and /app/my-registrations, which is where the
              desktop rail still links. See FeedSubPages. */}
          <Route path="events" element={<FeedEventsPage />} />
          <Route path="passes" element={<FeedPassesPage />} />
          <Route path="certificates" element={<MyCertificatesPage />} />
          <Route path="payments" element={<MyPaymentsPage />} />
          {/* Motion board: nothing creates an event_result without this screen.
              Hidden 2026-11-02, phase 7a — attaching belongs to organizers in this
              phase, in place on the board. Restore with the FeedSidebar entry. */}
          {/* <Route path="attach" element={<AttachRunPage />} /> */}
        </Route>

        {/* The "Certificate ready" email's download link: sign in if needed,
            then My certificates with that certificate highlighted. */}
        <Route path="/certificate/:certificateId" element={<PrivateRoute><CertificateLinkPage /></PrivateRoute>} />

        {/* Organizer surface — organizer_admin only, fixed Dawn palette.
            Entered from the post-login role prompt in SignInPage. */}
        <Route
          path="/organizer"
          element={(
            <OrganizerRoute>
              <OrganizerLayout />
            </OrganizerRoute>
          )}
        >
          {/* Home is hidden from the organizer rail for now, so /organizer —
              still reached from the public header and old links —
              forwards to Events. Flip ORGANIZER_HOME_ENABLED to restore it. */}
          <Route
            path=""
            element={ORGANIZER_HOME_ENABLED ? <OrganizerHome /> : <Navigate to="events" replace />}
          />
          <Route path="events" element={<OrganizerEventsPage />} />
          <Route path="events/create" element={<OrganizerEventFormPage />} />
          <Route path="events/:eventId/edit" element={<OrganizerEventFormPage />} />
          <Route path="results" element={<OrganizerResultsPage />} />
          <Route path="results/:raceId" element={<PublishResultsPage />} />
          <Route path="check-in" element={<OrganizerCheckInPage />} />
          <Route path="routes" element={<OrganizerRouteBuilderPage />} />
          <Route
            path="statistics"
            element={(
              <OrganizerPlaceholder
                title="Statistics"
                subtitle="Season performance across every event"
                body="Registrations per week against last season, retention cohorts and per-event revenue are designed and queued next."
              />
            )}
          />
          <Route
            path="settings"
            element={(
              <OrganizerPlaceholder
                title="Settings"
                subtitle="Event defaults, payouts and notifications"
                body="Organizer settings, including switching your active role back to the athlete view, come with this screen."
              />
            )}
          />
        </Route>
      </Routes>
      </AppErrorBoundary>
    </AlertDialogProvider>
  );
}

export default App;
